<template>
    <div>
        <template v-if="!question_code">
            <b-container class="p-0">
                <div class="py-5 text-center bgray-200 rounded">질문 리스트에서 편집할 질문을 선택해주세요.</div>
            </b-container>
        </template>

        <template v-else>
            <b-container class="px-0">
                <div
                    class="d-flex justify-content-between align-items-center border-bottom border-color-gray-300 f-120 fw-800 pt-2 mb-2">
                    <span v-show="!is_question_subs_fully_loaded && !load_error" class="gray-400">
                        (불러오는중)
                    </span>
                    <span v-show="load_error" class="text-danger">
                        (불러오기 실패)
                    </span>
                    <span v-show="is_question_subs_fully_loaded && !load_error">
                        {{ question_name }} {{ question_code }}
                    </span>
                    <div>
                        <b-button size="sm" class="mb-1" @click="openSubOrderChangeModal">문항 순서 변경</b-button>
                    </div>
                </div>

                <PageEmptyComponent v-if="load_error" compact :error="load_error" @retry="readQuestionContent(question_code)" />

                <b-card
                    v-else
                    no-body
                    class="border border-color-gray-300 border-style-dashed mb-4 p-1"
                    @click="openModifyModal('info_box')"
                >
                    <b-card-text
                        v-if="!isFieldEmpty(question_infobox)"
                        class="small"
                    >
                        <span v-show="$i18n.locale === 'ko'"> {{ question_infobox }} </span>
                        <span v-show="$i18n.locale !== 'ko'"> {{ question_infobox_foreign }} </span>
                    </b-card-text>
                    <b-card-text v-else>
                        클릭하여 질문의 배경 정보를 입력해 주세요(Click)
                    </b-card-text>
                </b-card>

                <template v-if="is_question_subs_fully_loaded === false && (question_subs && question_subs.length)">
                    <div class="d-flex justify-content-center align-items-center bgray-200 rounded py-5">
                        <h4 class="font-weight-normal">
                            <b-spinner class="mr-3"/>
                            <span>질문의 세부문항을 불러오고 있습니다.</span>
                        </h4>
                    </div>
                </template>


                <!-- sortable -->
                <div v-for="(sub, index) in question_subs"
                     :key="sub.sub_code || `new-sub-${index}`"
                     class="d-flex mb-5"
                >
                    <!-- 폭은 CSS 로만: lg 이상은 미리보기 60 / 채점 패널 40, 그 미만은 패널이 숨어 미리보기가 전체 폭 -->
                    <div
                        class="position-relative border border-color-gray-300 border-style-dashed cursor p-2"
                        style="flex: 1 1 0; min-width: 0;"
                    >
                        <div v-if="sub.sub_type" @click.stop="openModifyModal('sub_component', index)">
                            <sub-component
                                :sub_type="sub.sub_type"
                                :item="sub"
                                :preview="true"
                                :view_pipe_line="false"
                                :is_disabled_by_parent="true"
                                :is_edit_page="true"
                                @update-contents="handleUpdateContents(index)"
                            />
                        </div>
                        <div v-else
                             class="pl-2"
                             @click="openModifyModal('sub_component', index)"
                        >
                            클릭하여 세부 질문을 정의해 주세요(Click)
                        </div>
                        <div class="position-absolute" style="top: 0; right:0;">
                            <b-button
                                variant="red"
                                size="sm"
                                class=""
                                @click="openDeleteModal(sub)"
                            >
                                <fa icon="trash-alt"></fa>
                            </b-button>
                        </div>
                    </div>

                    <template v-if="sub.sub_type">
                        <div class="pl-3 d-none d-lg-block"
                             style="flex: 0 0 40%; max-width: 40%;"
                        >
                            <b-collapse
                                v-if="question_evaluations !== null"
                                :id="'method' + sub.sub_code"
                                class="bgray-100"
                                visible
                            >
                                <!-- 질문 단위 조회가 실패했거나(false) 그 뒤에 추가된 문항이면 undefined → 패널이 개별 조회한다 -->
                                <editing-scoring-method-v2
                                    :sub_code="sub.sub_code"
                                    :sub_type="sub.sub_type"
                                    :read_only="true"
                                    :initial_evaluations="question_evaluations ? question_evaluations[sub.sub_code] : undefined"
                                />
                            </b-collapse>
                        </div>
                    </template>
                </div>
            </b-container>
        </template>

        <template v-if="question_code">
            <div class="text-right pt-5">
                <b-button variant="blue" size="sm"
                          :disabled="is_empty_template_meta || !is_question_subs_fully_loaded || !!load_error"
                          @click="addSub"
                >
                    <fa icon="plus" class="mr-1"></fa>
                    새문항
                </b-button>
            </div>
        </template>

        <!-- sub component 생성/수정하기 모달 -->
        <b-modal
            id="modify-modal"
            ref="modify-modal"
            title="수정하기"
            hide-footer
            scrollable
            :size="use_v2 ? 'xl' : undefined"
            :no-close-on-backdrop="modal_mode === 'sub_component'"
            :dialog-class="use_v2 ? 'qm-v2-dialog' : ''"
            @hide="handleHide"
            @hidden="handleHidden"
        >
            <template v-if="modal_mode === 'info_box'">
                <div class="fw-400 p-2 text-left"> Infobox 수정</div>
                <p class="mb-1">※ 배경정보의 번역 편집은 번역편집 화면에서 하셔야 합니다.</p>
                <p>※ <strong>1. 질문 리스트 생성</strong> 에서 <strong>편집모드 버튼</strong> 클릭 후 하단 <strong>번역 버튼</strong> 클릭.</p>
                <b-form-textarea v-model="infobox_draft" class="p-2" rows="6" :disabled="is_saving_infobox"></b-form-textarea>
                <div class="mt-3 mb-3 mr-3 text-right">
                    <b-btn variant="dark" :disabled="is_empty_template_meta || is_saving_infobox"
                           @click="hideModal"
                    >
                        취소
                    </b-btn>
                    <b-btn class="ml-2" variant="blue" :disabled="is_empty_template_meta || is_saving_infobox"
                           @click="saveInfobox"
                    >
                        <b-spinner v-show="is_saving_infobox" small class="mr-1" />
                        저장
                    </b-btn>
                </div>
            </template>
            <template v-else-if="idx === -1 && modal_mode === 'sub_component'">
                <div style="min-height: 50vh">
                    <editing-others :idx="idx" @click-cancel="hideModal()" @click-save="handleOK"/>
                </div>
            </template>
            <template v-else-if="idx > -1 && modal_mode === 'sub_component'">
                <div style="min-height: 50vh">
                    <template v-if="!question_subs[idx].sub_code">
                        <editing-others :idx="idx" @click-cancel="hideModal()" @click-save="handleOK"/>
                    </template>
                    <template v-else>
                        <b-card no-body>
                            <!-- 지원 타입은 v2 한 화면(구조·설정·채점·번역)이 기본. 스위치로 구 편집기(Editing/Scoring/번역 탭)로 돌아갈 수 있다. -->
                            <div v-if="v2_supported" class="px-3 pt-2 text-right">
                                <b-form-checkbox :key="legacy_switch_key" :checked="use_legacy_editor" switch size="sm" inline @change="toggleLegacyEditor">구 편집기로 열기</b-form-checkbox>
                            </div>
                            <!-- :key — 스위치를 바꾸면 탭을 다시 만들어 첫 탭 활성·lazy 재마운트를 보장한다 -->
                            <!-- lazy 탭은 다른 탭으로 옮기면 내용이 사라진다 — 미저장 편집이 있으면 옮기기 전에 묻는다(onActivateTab) -->
                            <b-tabs :key="String(use_v2)" v-model="modal_tab" card content-class="p-3" active-nav-item-class="text-info" @activate-tab="onActivateTab">
                                <b-tab v-if="use_v2" no-body title="편집 (v2)" lazy>
                                    <sv2-editor
                                        :code="question_subs[idx].sub_code"
                                        @saved="handleV2Saved"
                                        @dirty="updatePreventHideModifyModal"
                                        @cancel="handleV2Cancel"
                                    />
                                </b-tab>
                                <b-tab v-if="!use_v2" no-body title="Editing" lazy>
                                    <editing-sub-component
                                        :label="question_subs[idx].label"
                                        :sub_code="question_subs[idx].sub_code"
                                        :sub_type="question_subs[idx].sub_type"
                                        @click-save="handleOK"
                                        @click-cancel="handleV2Cancel"
                                        @dirty="legacy_edit_dirty = $event"
                                    />
                                </b-tab>
                                <b-tab v-if="!use_v2" no-body title="Scoring" lazy>
                                    <editing-scoring-method-v2
                                        :sub_code="question_subs[idx].sub_code"
                                        :sub_type="question_subs[idx].sub_type"
                                        @on-prevent-modify-modal="updatePreventHideModifyModal"
                                        @scoring_method_saved="handleOK"
                                        @saved="saved_in_modal = true"
                                        @click-cancel="handleV2Cancel"
                                    />
                                </b-tab>
                                <b-tab no-body title="Pipeline" lazy>
                                    <editing-pipeline
                                        :sub_code="question_subs[idx].sub_code"
                                        :sub_type="question_subs[idx].sub_type"
                                        @click-cancel="hideModal()"
                                    />
                                </b-tab>
                                <b-tab no-body title="Figure" lazy>
                                    <editing-figure-definition
                                        :sub_code="question_subs[idx].sub_code"
                                        :sub_type="question_subs[idx].sub_type"
                                        @click-cancel="hideModal()"
                                    />
                                </b-tab>
                                <b-tab v-if="!use_v2" no-body title="번역" lazy>
                                    <editing-translation
                                        :sub_data="question_subs[idx]"
                                        :sub_code="question_subs[idx].sub_code"
                                        :sub_type="question_subs[idx].sub_type"
                                        @click-cancel="hideModal()"
                                    />
                                </b-tab>
                                <b-tab no-body title="Parser" lazy>
                                    <editingSubParserPicker
                                        :sub_data="question_subs[idx]"
                                        :sub_code="question_subs[idx].sub_code"
                                        :sub_type="question_subs[idx].sub_type"
                                        :sub_data_contents="question_subs[idx].sub_data_contents"
                                        @click-cancel="hideModal()"
                                    />
                                </b-tab>
                                <b-tab no-body title="Criteria Dictionary" lazy>
                                    <EditingCriteriaDictionary
                                        :sub_data="question_subs[idx]"
                                        :sub_code="question_subs[idx].sub_code"
                                        :sub_type="question_subs[idx].sub_type"
                                        :sub_data_contents="question_subs[idx].sub_data_contents"
                                        @click-cancel="hideModal()"
                                    />
                                </b-tab>
                            </b-tabs>
                        </b-card>
                    </template>
                </div>
            </template>
        </b-modal>

        <!-- 삭제경고 Modal -->
        <b-modal ref="delete-modal" header-text-variant="danger" title="경고" ok-variant="danger" ok-title="연결 해제" cancel-title="취소" @ok="unlinkSub(delete_target_code)">
            <p> 문항 <strong>{{ delete_target_label }}</strong> ({{ delete_target_code }})의 연결을 이 질문에서 해제합니다. 문항 자체는 삭제되지 않으며, '검색해서 가져오기'를 통해 다시 추가할 수 있습니다.</p>
            <p> 계속 하시겠습니까? </p>
        </b-modal>
        <SubOrderChangeModal :subs="question_subs"
                             :question_code="question_code"
                             :question_name="question_name"
                             @subsOrderChanged="readQuestionContent"
        />

    </div>
</template>

<script>
import {mapActions, mapState} from 'vuex';
import subComponent from '@core_components/client/components/subs/SubComponent.vue';
import editingPipeline from '@core_components/admin/components/subs/EditingPipeline';
import editingFigureDefinition from '@core_components/admin/components/subs/EditingFigureDefinition';
import editingTranslation from '@core_components/admin/components/subs/EditingTranslation';
import editingSubParserPicker from '@core_components/admin/components/subs/EditingSubParserPicker';
import EditingCriteriaDictionary from '@core_components/admin/components/subs/EditingCriteriaDictionary';
import editingSubComponent from '@core_components/admin/components/subs/editing/EditingSubComponent';
import EditingOthers from '@core_components/admin/components/subs/editing/EditingOthers';
import SubOrderChangeModal from '@core/components/admin/components/subs/SubOrderChangeModal.vue';
import EditingScoringMethodV2 from '@/components/editingTemplate/EditingScoringMethod_v2.vue';
// v2 편집기(구조·문항 설정·채점 기준·번역 한 화면). 지원 타입이면 기본, 아니면 구 탭 그대로.
import Sv2Editor from '@/components/structureV2/Sv2Editor.vue';
import {SUPPORTED_SUB_TYPES} from '@core_utils/subComponent/structure-tree.js';
import tempEvCode from './temp_ev_code.json';
import {waitTime} from '@shared/utils/common';
import PageEmptyComponent from '@shared/components/admin/components/common/PageEmptyComponent.vue';
import {getErrorMessage} from '@core/utils/api/errorMessage';

export default {
    name: 'QuestionMasterV2',
    props: {},
    components: {
        PageEmptyComponent,
        Sv2Editor,
        EditingScoringMethodV2,
        SubOrderChangeModal,
        subComponent,
        editingSubComponent,
        editingPipeline,
        EditingOthers,
        editingFigureDefinition,
        editingTranslation,
        editingSubParserPicker,
        EditingCriteriaDictionary
    },
    data() {
        return {
            is_disabled: false,
            modal_mode: false,
            idx: -1,
            delete_target_code: '',
            delete_target_label: '',
            infobox_draft: '',
            is_saving_infobox: false,
            legacy_switch_key: 0,
            load_error: '',
            // show_sub_component: true,
            // show_scoring_method: false,
            // show_pipeline: false,
            // show_figure_definition: false,
            // 목록 채점 패널용 질문 단위 평가요소 {sub_code: [...]}. null = 불러오는 중, false = 실패(패널이 개별 조회)
            question_evaluations: null,
            // v2 편집기·Scoring 탭의 미저장 표시
            is_prevent_hide_modifyModal: false,
            // 구 편집기 Editing 탭의 미저장 표시. Scoring 탭이 늦게 열리며 false 를 보내도 지워지지 않게 따로 둔다
            legacy_edit_dirty: false,
            modal_tab: 0,
            // v2 → 구 편집기 전환 스위치. 설정이 아니라 전환기 안전망이라 모달을 열 때마다 리셋한다.
            use_legacy_editor: false,
            // v2 에서 한 번이라도 저장했으면 모달이 닫힌 뒤 전체 재조회(목록의 채점 패널까지 리마운트)
            saved_in_modal: false
        };
    },
    computed: {
        ...mapState(['is_question_subs_loaded', 'template_tree']),
        ...mapState('questions', ['subs_loaded']),
        question_composition() {
            return this.$store.getters.getQuestionComposition;
        },
        question_code() {
            return this.question_composition.question_code || this.$route.query.question_id;
        },
        question_name() {
            const {question_name, question_name_e, question_name_c} = this.question_composition;
            switch (this.$i18n.locale) {
            case 'ko':
                return question_name;
            case 'ca':
                return question_name_c;
            default:
                return question_name_e;
            }
        },
        question_subs() {
            return JSON.parse(JSON.stringify(this.$store.state.question_subs));
        },
        /** 지금 고른 문항을 v2 가 변환할 수 있는가 (file_URL2 등은 전용 변환이 없어 구 편집기) */
        v2_supported() {
            const s = this.idx > -1 && this.question_subs[this.idx];
            return !!(s && s.sub_code && SUPPORTED_SUB_TYPES.includes(s.sub_type));
        },
        use_v2() {
            return this.v2_supported && !this.use_legacy_editor;
        },
        question_infobox() {
            return this.question_composition.question_infobox;
        },
        has_unsaved_edit() {
            return this.is_prevent_hide_modifyModal || this.legacy_edit_dirty;
        },
        /** 닫기 확인이 필요한 모달 상태: v2/Scoring/구 Editing 은 dirty 플래그가 있고, 그 외 편집 탭은 변경 감지가 없어 열려 있는 것 자체를 미저장 위험으로 본다 */
        needs_close_confirm() {
            return this.has_unsaved_edit
                || (this.modal_mode === 'sub_component' && !this.use_v2)
                || (this.modal_mode === 'info_box' && this.infobox_draft !== (this.question_infobox || ''));
        },
        question_infobox_foreign() {
            const {question_infobox_e, question_infobox_c} = this.question_composition;
            switch (this.$i18n.locale) {
            case 'ca':
                return question_infobox_c;
            default:
                return question_infobox_e;
            }
        },
        is_question_subs_fully_loaded() {
            return this.is_question_subs_loaded;
        },
        is_empty_template_meta() {
            return this.isFieldEmpty(this.template_tree.template_name) || this.isFieldEmpty(this.template_tree.template_type);
        }
    },
    watch: {
        // 상위 화면(EditingTemplate_v2)의 이탈 가드가 쓴다
        has_unsaved_edit(_state) {
            this.$emit('modal-dirty', _state);
        },
        // 질문 트리 저장(순서/이름 변경)이 store 의 선택 질문을 비운다(update_template_tree). URL 은 그대로이므로 다시 읽어 채운다.
        'question_composition.question_code'(_code) {
            const _route_code = this.$route.query.question_id;
            const _in_tree = (nodes) => (nodes || []).some(node => node.question_code === _route_code || _in_tree(node.children));
            // 문항 로딩 중이라도 질문을 읽은 뒤(composition_read_done)에 비워지면 다시 읽는다. 읽기 시작 시 스스로 비우는 경우는 제외.
            if (!_code && _route_code && (this.is_question_subs_loaded || this.composition_read_done) && !this.load_error && _in_tree(this.template_tree.template_contents)) {
                this.readQuestionContent(_route_code);
            }
        },
        '$route.query.question_id': {
            async handler(_current_id) {
                // 열린 모달의 question_subs[idx] 는 목록이 비워지면 깨진다. 보통은 상위 beforeRouteUpdate 가 먼저 닫는다.
                if (this.modal_mode) this.hideModal();
                if (_current_id) {
                    await this.readQuestionContent(_current_id);
                } else {
                    this.$store.commit('update_is_question_subs_loaded', true);
                }
            }
        }
    },
    async created() {
        this.read_queue = Promise.resolve();
        this.composition_read_done = false;
        const _question_code = this.$route.query.question_id;
        await this.fetchEvCode();
        if (_question_code) {
            await this.readQuestionContent(_question_code);
        } else {
            this.$store.commit('update_is_question_subs_loaded', true);
        }
    },
    beforeDestroy() {
        this.$store.commit('update_question_composition', null);
        this.$store.commit('reset_question_subs');
    },
    methods: {
        ...mapActions('evaluations', ['fetchEvCode']),
        /** 질문 전환이 빨라도 응답이 섞이지 않도록 요청을 직렬화하고, 차례가 왔을 때 최신 질문이 아니면 건너뛴다 */
        readQuestionContent(question_code) {
            this.latest_read_code = question_code;
            const run = async () => {
                if (this.latest_read_code !== question_code) return;
                this.load_error = '';
                this.composition_read_done = false;
                this.$store.commit('update_is_question_subs_loaded', false);
                this.$store.commit('update_question_composition', null);
                this.$store.commit('reset_question_subs');
                this.question_evaluations = null;
                try {
                    await this.$store.dispatch('readQuestionComposition', question_code);
                    this.composition_read_done = true;
                    // 둘 다 끝날 때까지 기다린다(문항 조회가 먼저 실패해도 다음 질문 조회와 평가요소 응답이 섞이지 않게)
                    const [subs_error, evaluations] = await Promise.all([
                        this.$store.dispatch('readQuestionSubsInit').then(() => null, (error) => error || new Error('문항 조회 실패')),
                        this.readQuestionEvaluations(question_code)
                    ]);
                    if (subs_error) throw subs_error;
                    // 응답에는 평가요소가 있는 문항만 온다. 이때 불러온 문항은 빈 배열로 채우고,
                    // 나중에 검색으로 추가된 문항(키 없음 → undefined)은 패널이 개별 조회한다.
                    if (evaluations) {
                        this.$store.state.question_subs.forEach((_sub) => {
                            if (_sub.sub_code && !evaluations[_sub.sub_code]) evaluations[_sub.sub_code] = [];
                        });
                    }
                    this.question_evaluations = evaluations;
                } catch (error) {
                    this.load_error = `질문 내용을 불러오지 못했습니다. (${getErrorMessage(error, error && error.message ? error.message : '알 수 없는 오류')})`;
                } finally {
                    if (this.question_evaluations === null) this.question_evaluations = false;
                    this.composition_read_done = false;
                    this.$store.commit('update_is_question_subs_loaded', true);
                }
            };
            this.read_queue = this.read_queue.then(run);
            return this.read_queue;
        },
        /** 목록 채점 패널용. 문항마다 GET /evaluation/{sub_code} 를 보내던 것을 질문 단위 1건으로.
         *  {sub_code: [...]} 또는 실패 시 false 를 돌려준다(throw 하지 않음 — 패널이 개별 조회로 폴백) */
        async readQuestionEvaluations(question_code) {
            try {
                const {data} = await this.$axios.get(`/evaluation/question/${encodeURIComponent(question_code)}`);
                return data && typeof data === 'object' && !Array.isArray(data) ? {...data} : false;
            } catch (error) {
                // 문항 0개 질문은 404 — 그때는 그릴 패널도 없다
                if (!(error && error.response && error.response.status === 404)) {
                    console.error('[QuestionMasterV2] 질문 단위 평가요소 조회 실패 — 문항별 조회로 대체', error);
                }
                return false;
            }
        },
        async saveInfobox() {
            const previous = this.question_infobox;
            if (this.infobox_draft === (previous || '')) return this.handleOK();
            this.is_saving_infobox = true;
            try {
                this.$store.commit('update_question_infobox', this.infobox_draft);
                await this.$store.dispatch('saveQuestionComposition');
                this.$EventBus.$emit('toast-success', '성공', '질문 배경정보를 저장하였습니다.');
                this.handleOK();
            } catch (error) {
                this.$store.commit('update_question_infobox', previous);
                this.$EventBus.$emit('toast-failure', '질문 배경정보 저장 실패', getErrorMessage(error, '저장하지 못했습니다. 입력한 내용은 그대로 두었으니 다시 시도해주세요.'));
            } finally {
                this.is_saving_infobox = false;
            }
        },
        async unlinkSub(delete_target_code) {
            try {
                await this.$store.dispatch('unlinkSub', {sub_code: delete_target_code});
                this.$EventBus.$emit('toast-success', '성공', '문항 연결을 해제하였습니다. (문항 자체는 삭제되지 않았습니다)');
            } catch (error) {
                this.$EventBus.$emit('toast-failure', '문항 연결 해제 실패', getErrorMessage(error, '서버에 저장하지 못했습니다. 목록을 다시 불러옵니다.'));
                // 화면에서 먼저 지워졌으므로 서버 상태로 되돌린다
                await this.readQuestionContent(this.question_code);
            }
        },
        async toggleLegacyEditor(value) {
            if (this.has_unsaved_edit && !(await this.confirmDiscard('편집기를 전환하면 저장하지 않은 편집 내용이 사라집니다. 전환하시겠습니까?'))) {
                this.legacy_switch_key++;
                return;
            }
            this.is_prevent_hide_modifyModal = false;
            this.legacy_edit_dirty = false;
            this.modal_tab = 0;
            this.use_legacy_editor = value;
        },
        confirmDiscard(message = '수정한 데이터를 저장하지 않고 닫으면 데이터가 유실될 수 있습니다. 그래도 닫으시겠습니까?') {
            return Promise.resolve(this.$showModal({type: 'confirm', state: 'warning', title: '저장하지 않은 편집', message})).then(Boolean);
        },
        async handleOK() {
            this.idx = -1;
            this.modal_mode = false;
            this.$refs['modify-modal'].hide();
        },
        hideModal() {
            this.idx = -1;
            this.modal_mode = false;
            // 닫힘 방지 플래그는 이 모달 세션의 것. 남겨두면 다음에 연 문항에서 아무것도 안 고쳐도 confirm 이 뜬다.
            this.updatePreventHideModifyModal(false);
            this.legacy_edit_dirty = false;
            this.$refs['modify-modal'].hide();
        },
        /** 탭을 옮기면 lazy 탭의 편집 내용이 사라지므로, 미저장이면 막고 물은 뒤 확인하면 옮긴다(v-model 로 다시 활성화) */
        onActivateTab(new_index, prev_index, bvEvent) {
            if (!this.has_unsaved_edit) return;
            bvEvent.preventDefault();
            this.confirmDiscard('다른 탭으로 옮기면 저장하지 않은 편집 내용이 사라집니다. 옮기시겠습니까?').then((ok) => {
                if (!ok) return;
                this.updatePreventHideModifyModal(false);
                this.legacy_edit_dirty = false;
                this.modal_tab = new_index;
            });
        },
        /** v2 저장바의 '닫기'·구 편집기 Editing/Scoring 의 [취소] — 저장 안 한 변경이 있으면 묻고 닫는다 */
        async handleV2Cancel() {
            if (this.has_unsaved_edit && !(await this.confirmDiscard())) return;
            this.hideModal();
        },
        openDeleteModal(sub) {
            this.delete_target_code = sub.sub_code;
            this.delete_target_label = sub.label || sub.sub_name || sub.sub_code;
            this.$refs['delete-modal'].show();
        },
        addSub() {
            if (!this.question_code) {
                return alert('질문 리스트를 먼저 만들어 주세요.');
            }

            this.openModifyModal('sub_component');
        },
        openModifyModal(mode, index = -1) {
            this.use_legacy_editor = false;
            this.modal_tab = 0;
            this.saved_in_modal = false;
            this.is_prevent_hide_modifyModal = false;
            this.legacy_edit_dirty = false;
            this.modal_mode = mode;
            this.idx = index;
            this.infobox_draft = this.question_infobox || '';
            this.$refs['modify-modal'].show();
        },
        /** v2 저장 성공. 편집기는 자기 원본을 이미 재조회했고, 뒤 목록(question_subs)은 여기서 갱신한다.
         *  readQuestionSubsInit 은 배열을 비우지 않으므로 모달을 연 채 불러도 안전하다. */
        async handleV2Saved() {
            this.saved_in_modal = true;
            try {
                await this.$store.dispatch('readQuestionSubsInit');
            } catch (error) {
                // 저장은 이미 됐다. 목록만 낡은 것이니 닫을 때의 전체 재조회(handleHidden)에 맡기고 흔적만 남긴다.
                console.error('[QuestionMasterV2] v2 저장 후 목록 재조회 실패', error);
            }
        },
        /** 모달이 완전히 닫힌 뒤. 저장이 있었으면 전체 재조회 — readQuestionContent 는 목록을 비웠다 채우므로
         *  열린 상태에서 부르면 question_subs[idx] 렌더가 깨진다. 닫힌 뒤에만. */
        handleHidden() {
            if (this.saved_in_modal) {
                this.saved_in_modal = false;
                this.readQuestionContent(this.question_code);
            }
        },
        toggleDisabled(x) {
            this.is_disabled = x;
        },
        togglePreviewing() {
            this.is_previewing = !this.is_previewing;
        },
        handleHide(bvModalEvent) {
            if (bvModalEvent.trigger === 'esc' || bvModalEvent.trigger === 'backdrop' || bvModalEvent.trigger === 'headerclose') {
                if (this.needs_close_confirm) {
                    bvModalEvent.preventDefault();
                    this.confirmDiscard().then((ok) => ok && this.hideModal());
                    return false;
                }
            }

            this.hideModal();
        },
        updatePreventHideModifyModal(_state) {
            this.is_prevent_hide_modifyModal = _state;
        },
        openSubOrderChangeModal(){
            this.$bvModal.show('sub-order-change-modal');
        }
    }
};
</script>

<style>
/* v2 편집기 모달 — modal-xl(1140px)은 미리보기·구조·인스펙터 세 기둥에 비좁다. v2 의 미디어쿼리는
   뷰포트 기준이라 모달 폭에 반응하지 않으므로 다이얼로그 자체를 넓힌다 (SupplementReview 와 같은 방식). */
@media (min-width: 1200px) {
    .modal-dialog.qm-v2-dialog { max-width: min(1600px, calc(100vw - 2rem)); }
}
/* .bottom-blue {
    border-bottom: 1px solid var(--blue);
} */
/* .bottom-red {
    border-bottom: 1px solid var(--red);
} */

</style>
